import { ChatUserRolesEnum } from 'constants/chats';
import { FullMessageType, wsMessageTypeEnum } from 'hooks/useWebsocketTypes';
import { getInitialsFromFullname } from 'services/chat.sevice';
import i18n from 'shared/i18n';
import { CallUserType, UserItemType } from 'types/callType';
import {
  ChatDescription,
  ChatInterlocutor,
  ChatPreviewType,
  ChatUser,
  ChatUserParsed,
  DialogType,
  NewNotificationInfo,
  SelectedDialogInfo,
  SelectedDialogUserInfo,
} from 'types/chatsType';
import { FileDescriptor, MimeTypeValues } from 'types/fileTypes';
import { isMediaFile } from './files.utils';

type GetDialogParams = {
  chats: ChatDescription[];
  dialogId: number;
};
type GetUserParams = {
  dialog?: ChatDescription;
  userId: string | null | undefined;
  users?: SelectedDialogUserInfo[];
};

export const getDialog = ({ chats, dialogId }: GetDialogParams): ChatDescription | undefined =>
  chats.find((dialog) => dialog.id === dialogId);

export const getUser = ({
  dialog,
  userId,
  users,
}: GetUserParams): ChatInterlocutor | SelectedDialogUserInfo | undefined => {
  if (!userId) {
    return undefined;
  }

  const fromUsers = users?.find((user) => user.userId === userId);
  if (fromUsers) {
    return fromUsers;
  }

  if (dialog?.interlocutor?.userId === userId) {
    return dialog.interlocutor;
  }

  return undefined;
};

type GetNotificationInfoParams = {
  dialogId: number;
  messageId?: number;
  user?: ChatInterlocutor | ChatUser | SelectedDialogUserInfo | null;
};

export const getNotificationInfo = ({
  dialogId,
  messageId,
  user,
}: GetNotificationInfoParams): NewNotificationInfo | null => {
  if (!user || !messageId) {
    return null;
  }
  return {
    dialogId,
    id: `${dialogId}-${messageId}`,
    img: user.image?.path || '',
    userName: user.fullName || user.nickname,
  };
};

export const getCaller = ({
  dialog,
  userId,
  users,
}: {
  dialog?: ChatDescription | SelectedDialogInfo;
  userId?: string | null;
  users?: SelectedDialogUserInfo[];
}): CallUserType | null => {
  const user: SelectedDialogUserInfo | undefined =
    dialog?.type === DialogType.group
      ? users?.find((item) => item.userId === userId)
      : dialog?.interlocutor;

  return user ? { ...user, isAdmin: user.isAdmin ? 1 : 0 } : null;
};

export const checkIsAdded = (el: UserItemType, arr: UserItemType[]): boolean =>
  Boolean(arr.find((element) => element.userId === el.userId));

export const parseUser = ({
  userId,
  fullName,
  imageUrl,
  status,
  role,
}: {
  userId: ChatInterlocutor['userId'];
  fullName: ChatInterlocutor['fullName'];
  imageUrl?: FileDescriptor['path'];
  status?: ChatDescription['status'];
  role: ChatUserRolesEnum;
}): ChatUserParsed => ({
  imageUrl,
  initials: getInitialsFromFullname(fullName),
  role,
  status,
  userId,
  userName: fullName,
});

export const reorderMsgAttachment = (files: FileDescriptor[]): { media: FileDescriptor[]; rest: FileDescriptor[] } => {
  const media = files.filter((file) => isMediaFile(file.fileType as MimeTypeValues));
  const rest = files.filter((file) => !isMediaFile(file.fileType as MimeTypeValues));
  return { media: media || [], rest: rest || [] };
};

export const SELECTABLE_MESSAGE_TYPES = [
  wsMessageTypeEnum.textMessage,
  wsMessageTypeEnum.voiceMessage,
  wsMessageTypeEnum.sentContact,
  wsMessageTypeEnum.poll,
];

const PREVIEW_TEXT_KEY_BY_MESSAGE_TYPE: Partial<Record<wsMessageTypeEnum, string>> = {
  [wsMessageTypeEnum.voiceMessage]: 'page.chat.preview.voice',
  [wsMessageTypeEnum.sentContact]: 'page.chat.preview.contact',
  [wsMessageTypeEnum.audioCallStarted]: 'page.chat.preview.audioCall',
  [wsMessageTypeEnum.videoCallStarted]: 'page.chat.preview.videoCall',
  [wsMessageTypeEnum.callEnded]: 'page.chat.preview.callEnded',
  [wsMessageTypeEnum.missedCall]: 'page.chat.preview.missedCall',
  [wsMessageTypeEnum.notAnswer]: 'page.chat.preview.notAnswer',
};

type ResolvePreviewTextParams = {
  fileName?: string;
  messageType: wsMessageTypeEnum;
  pollQuestion?: string;
  text?: string | null;
};

export const resolvePreviewText = ({
  fileName,
  messageType,
  pollQuestion,
  text,
}: ResolvePreviewTextParams): string => {
  const trimmedText = text?.trim();
  const textOrFileName = () => trimmedText || fileName?.trim() || '';

  if (messageType === wsMessageTypeEnum.textMessage) {
    return textOrFileName();
  }
  if (messageType === wsMessageTypeEnum.poll) {
    return pollQuestion?.trim() || trimmedText || i18n.t('poll');
  }
  const previewKey = PREVIEW_TEXT_KEY_BY_MESSAGE_TYPE[messageType];
  if (previewKey) {
    return i18n.t(previewKey);
  }
  return textOrFileName();
};

export const normalizeChatPreview = (preview: ChatPreviewType): ChatPreviewType => ({
  ...preview,
  t: resolvePreviewText({
    fileName: preview.f?.fileName,
    messageType: preview.mt,
    text: preview.t,
  }),
});

export const normalizeDialogsPreviews = <T extends { preview?: ChatPreviewType }>(dialogs: T[]): T[] =>
  dialogs.map((dialog) =>
    dialog.preview ? { ...dialog, preview: normalizeChatPreview(dialog.preview) } : dialog
  );

type WsPreviewMessage = Pick<FullMessageType, 'id' | 'cType' | 'createdAt' | 'owner' | 'files' | 'poll'> &
  Partial<Pick<FullMessageType, 'text'>>;

const getPreviewText = (fullMessage: WsPreviewMessage): string =>
  resolvePreviewText({
    fileName: fullMessage.files?.[0]?.fileName,
    messageType: fullMessage.cType,
    pollQuestion: fullMessage.poll?.s,
    text: fullMessage.text,
  });

export const getChatPreviewFromWsMessage = (
  fullMessage: WsPreviewMessage,
  chat: ChatDescription,
  users?: SelectedDialogUserInfo[]
): ChatPreviewType => {
  const author = getUser({ dialog: chat, userId: fullMessage.owner, users });
  const files = fullMessage.files;
  const authorId = author?.userId || fullMessage.owner || '';

  const preview: ChatPreviewType = {
    cd: fullMessage.createdAt ?? new Date().toISOString(),
    i: fullMessage.id,
    mt: fullMessage.cType,
    o: {
      fn: author?.fullName || '',
      i: authorId,
      im: author?.image?.path || undefined,
    },
    t: getPreviewText(fullMessage),
    ud: authorId,
  };

  if (files?.length) {
    const file = files[0];
    preview.f = { fileName: file.fileName, fileType: file.fileType, path: file.path };
  }

  return preview;
};

type DialogListPayload = {
  messageId?: number;
  preview?: ChatPreviewType;
  unreadMessageCount?: number;
};

const compareChatsByPinnedAndMessageId = (a: ChatDescription, b: ChatDescription): number => {
  if (a.isPinned !== b.isPinned) {
    return Number(b.isPinned) - Number(a.isPinned);
  }
  return b.messageId - a.messageId;
};

export const sortChatsByPinAndLastMessage = (chats: ChatDescription[]): ChatDescription[] =>
  [...chats].sort(compareChatsByPinnedAndMessageId);

export const mergeDialogListFields = (chat: ChatDescription, payload: DialogListPayload): ChatDescription => {
  const normalizedPreview = payload.preview != null ? normalizeChatPreview(payload.preview) : undefined;
  const unreadFromPayload = payload.unreadMessageCount;
  const unreadMessageCount = unreadFromPayload != null ? unreadFromPayload : chat.unreadMessageCount ?? 0;
  const messageId = payload.messageId ?? payload.preview?.i;
  const hasPreviewText = Boolean(normalizedPreview?.t?.trim());
  const isNewerPreview = normalizedPreview?.i != null && normalizedPreview.i !== chat.preview?.i;

  return {
    ...chat,
    unreadMessageCount,
    ...(normalizedPreview != null && (hasPreviewText || isNewerPreview) && { preview: normalizedPreview }),
    ...(messageId != null && { messageId }),
  };
};

const toListInterlocutor = (
  user: SelectedDialogUserInfo | ChatInterlocutor | undefined | null
): ChatInterlocutor | undefined => {
  if (!user) {
    return undefined;
  }
  return {
    fullName: user.fullName,
    image: user.image,
    nickname: user.nickname,
    status: user.status,
    userId: user.userId,
  };
};

export const applySelectedDialogInfoToListItem = (
  chat: ChatDescription,
  payload: SelectedDialogInfo
): ChatDescription => {
  const { users: _users, ...listChat } = chat as ChatDescription & { users?: unknown };
  const name = payload.name !== undefined ? payload.name : listChat.name;
  const image = payload.image !== undefined ? payload.image : listChat.image;
  const interlocutor = toListInterlocutor(payload.interlocutor) ?? listChat.interlocutor;

  return {
    ...listChat,
    activeCall: payload.activeCall ?? listChat.activeCall,
    firstMessageId: payload.firstMessageId ?? listChat.firstMessageId,
    id: payload.id,
    image: image ?? null,
    interlocutor,
    isBlocked: payload.isBlocked ?? listChat.isBlocked,
    isDeleted: payload.isDeleted ?? listChat.isDeleted,
    isPinned: payload.isPinned ?? listChat.isPinned,
    isUnread: payload.isUnread ?? listChat.isUnread,
    lastReadMessageId: payload.lastReadMessageId ?? listChat.lastReadMessageId,
    name: name ?? null,
    type: payload.type,
    ...(payload.unreadMessageCount != null && {
      unreadMessageCount: payload.unreadMessageCount,
    }),
  };
};

export const createChatListItemFromSelectedDialogInfo = (payload: SelectedDialogInfo): ChatDescription =>
  applySelectedDialogInfoToListItem(
    {
      id: payload.id,
      image: null,
      index: 1,
      interlocutor: undefined,
      isBlocked: false,
      isPinned: payload.isPinned ?? false,
      messageId: payload.messageId ?? 0,
      name: null,
      notificationsDisable: false,
      type: payload.type,
      unreadMessageCount: payload.unreadMessageCount ?? 0,
    },
    payload
  );

type DialogApiListItem = ChatDescription & { users?: unknown };

export const stripUsersFromDialogs = (dialogs: DialogApiListItem[]): ChatDescription[] =>
  dialogs.map(({ users: _users, ...dialog }) => dialog);
